import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export interface SitePage {
  id: string;
  slug: string;
  title: string;
  body_markdown: string;
  is_published: boolean;
  show_in_nav: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface SiteBlock {
  key: string;
  value: unknown;
  description: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

// Public hooks (for visitors)
export function useCmsPage(slug: string | undefined) {
  const query = useQuery({
    queryKey: ['cms-page', slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('site_pages')
        .select('slug, title, body_markdown, updated_at, created_at')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  return {
    page: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useCmsNavPages() {
  return useQuery({
    queryKey: ['cms-nav-pages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_pages')
        .select('slug, title, updated_at')
        .eq('is_published', true)
        .eq('show_in_nav', true)
        .order('title');

      if (error) throw error;
      return data || [];
    },
  });
}

export function useCmsBlock(key: string) {
  return useQuery({
    queryKey: ['cms-block', key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_blocks')
        .select('key, value, description, updated_at')
        .eq('key', key)
        .eq('is_public', true)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });
}

// Admin hooks
export function useCmsAdminPages() {
  return useQuery({
    queryKey: ['cms-admin-pages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_pages')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return (data || []) as SitePage[];
    },
  });
}

export function useCmsAdminPageBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['cms-admin-page', slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('site_pages')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      return data as SitePage | null;
    },
    enabled: !!slug,
  });
}

export function useCmsAdminBlocks() {
  return useQuery({
    queryKey: ['cms-admin-blocks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_blocks')
        .select('*')
        .order('key');

      if (error) throw error;
      return (data || []) as SiteBlock[];
    },
  });
}

export function useCmsAdminMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createPage = useMutation({
    mutationFn: async (page: Omit<SitePage, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>) => {
      const { data, error } = await supabase
        .from('site_pages')
        .insert(page)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-admin-pages'] });
      queryClient.invalidateQueries({ queryKey: ['cms-nav-pages'] });
      toast({ title: 'Page created' });
    },
    onError: (error: any) => {
      toast({ title: 'Error creating page', description: error.message, variant: 'destructive' });
    },
  });

  const updatePage = useMutation({
    mutationFn: async ({ id, ...page }: Partial<SitePage> & { id: string }) => {
      const { data, error } = await supabase
        .from('site_pages')
        .update(page)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-admin-pages'] });
      queryClient.invalidateQueries({ queryKey: ['cms-nav-pages'] });
      queryClient.invalidateQueries({ queryKey: ['cms-page'] });
      toast({ title: 'Page updated' });
    },
    onError: (error: any) => {
      toast({ title: 'Error updating page', description: error.message, variant: 'destructive' });
    },
  });

  const deletePage = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('site_pages')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-admin-pages'] });
      queryClient.invalidateQueries({ queryKey: ['cms-nav-pages'] });
      toast({ title: 'Page deleted' });
    },
    onError: (error: any) => {
      toast({ title: 'Error deleting page', description: error.message, variant: 'destructive' });
    },
  });

  const upsertBlock = useMutation({
    mutationFn: async (block: { key: string; value: unknown; description: string | null; is_public: boolean }) => {
      const { data, error } = await supabase
        .from('site_blocks')
        .upsert({
          key: block.key,
          value: block.value as any,
          description: block.description,
          is_public: block.is_public,
        }, { onConflict: 'key' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-admin-blocks'] });
      queryClient.invalidateQueries({ queryKey: ['cms-block'] });
      toast({ title: 'Block saved' });
    },
    onError: (error: any) => {
      toast({ title: 'Error saving block', description: error.message, variant: 'destructive' });
    },
  });

  const deleteBlock = useMutation({
    mutationFn: async (key: string) => {
      const { error } = await supabase
        .from('site_blocks')
        .delete()
        .eq('key', key);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-admin-blocks'] });
      toast({ title: 'Block deleted' });
    },
    onError: (error: any) => {
      toast({ title: 'Error deleting block', description: error.message, variant: 'destructive' });
    },
  });

  return {
    createPage: createPage.mutate,
    updatePage: updatePage.mutate,
    deletePage: deletePage.mutate,
    upsertBlock: upsertBlock.mutate,
    deleteBlock: deleteBlock.mutate,
    isLoading: createPage.isPending || updatePage.isPending || deletePage.isPending || upsertBlock.isPending || deleteBlock.isPending,
  };
}
